import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Carousel, CarouselVariant, Collapse, SliderNavColor, SliderNavVariant, POPOVER_BODY_SELECTOR, MediaQuery, } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { BasketStatus, LazyLoadOptions, } from '@as-react/common-types';
import { TextAlpha, useMediaQueryMatchesOnMobile, Box } from '@yonderland/andes-react';
import { useMemo, useState, useEffect, useId, useRef } from 'react';
import { MIN_SCREEN_WIDTH_TABLET, MIN_SCREEN_WIDTH_DESKTOP, MIN_SCREEN_WIDTH_DESKTOP_LARGE, } from '../../constants/ScreenWidthConstants';
import { SLIDES_AMOUNT_DESKTOP, SLIDES_AMOUNT_DESKTOP_LARGE, SLIDE_SPACING } from '../../constants/SliderConstants';
import { useProductGroupAnalytics } from '../../hooks/useProductGroupAnalytics';
import { isExtensiveProductTile, isClickAndCollectOnly, isInStoreOnly } from '../../util/ProductUtil';
import { generateTestId } from '../../util/TestUtil';
import { ProductTileVertical } from '../product-tile/vertical/ProductTileVertical';
const TEST_ID = 'product-group';
export const ProductGroup = ({ addToBasket, analyticsAreaName, basketPopup, basketStatus, collapsed, collapsible, compareItems, deliveryPromiseExplanations, getDeliveryPromise, hideBasketPopup, productGroupId, inBasketOverview, numberOfTiles, onAddToBasketCallback, onTileClick, onWishListToggle, products, productTileFormat = 'normal', recommendedProduct, showComparison, title = '', updateCompareList, analyticsProductGroupName = '', monetateId, removeOuterSpacing, enablePriceReference, }) => {
    const { isShopLanding } = useAemConfig();
    const isMobile = useMediaQueryMatchesOnMobile();
    const [currentMobileState, setCurrentMobileState] = useState(false);
    const productUniqueKey = useId();
    const productGroupRef = useRef(null);
    const isExtensiveTile = isExtensiveProductTile(productTileFormat);
    const { handleItemClickAnalytics, getProductGroupAnalyticsData } = useProductGroupAnalytics(Boolean(analyticsProductGroupName), productGroupRef, analyticsProductGroupName, title, products);
    useEffect(() => {
        setCurrentMobileState(isMobile);
    }, [isMobile]);
    const slides = useMemo(() => products.map((product, index) => {
        const handleAddToBasket = addToBasket
            ? (productData) => {
                addToBasket(productData, getProductGroupAnalyticsData(index + 1));
            }
            : undefined;
        const handleTileClick = (e, productData, color) => {
            onTileClick === null || onTileClick === void 0 ? void 0 : onTileClick(e, productData, color);
            handleItemClickAnalytics(index + 1, product);
        };
        return (_jsx("div", { children: _jsx(ProductTileVertical, { addToBasket: handleAddToBasket, alwaysShowSwatches: isShopLanding, clickAndCollectOnly: isClickAndCollectOnly(product), compareItems: compareItems, deliveryPromiseExplanations: deliveryPromiseExplanations, dropdownProps: { appendToBody: true }, getDeliveryPromise: getDeliveryPromise, hideBasketPopup: hideBasketPopup, inStoreOnly: isInStoreOnly(product), onAddToBasketCallback: onAddToBasketCallback, onWishListToggle: onWishListToggle, parentId: productGroupId, product: product, format: productTileFormat, recommendedProduct: recommendedProduct, scrollableWrapperSelector: basketPopup ? POPOVER_BODY_SELECTOR : undefined, shouldLazyLoadImage: LazyLoadOptions.LAZY_LOAD, showComparison: showComparison, showProductOutOfStockOverlay: isExtensiveTile, showSwatchesOnHover: !isShopLanding, updateCompareList: updateCompareList, onTileClick: handleTileClick, addDataMonetateProductId: Boolean(monetateId), enablePriceReference: enablePriceReference }) }, `${productUniqueKey}-${product.productCode}_${product.colourId}`));
    }), [products]);
    if (!(products === null || products === void 0 ? void 0 : products.length)) {
        return null;
    }
    const ITEMS_AMOUNT_TABLET = (numberOfTiles === null || numberOfTiles === void 0 ? void 0 : numberOfTiles.tablet) || 'auto';
    const ITEMS_AMOUNT_DESKTOP = (numberOfTiles === null || numberOfTiles === void 0 ? void 0 : numberOfTiles.desktop) || SLIDES_AMOUNT_DESKTOP;
    const ITEMS_AMOUNT_DESKTOP_LARGE = (numberOfTiles === null || numberOfTiles === void 0 ? void 0 : numberOfTiles.desktopLarge) || SLIDES_AMOUNT_DESKTOP_LARGE;
    const getCarouselVariant = () => {
        if ((inBasketOverview && basketStatus !== BasketStatus.EMPTY) || basketPopup) {
            return isExtensiveTile ? CarouselVariant.PRODUCT_TILE_AUTO_EXTENSIVE : CarouselVariant.PRODUCT_TILE_AUTO;
        }
        else {
            return isExtensiveTile ? CarouselVariant.PRODUCT_TILE_EXTENSIVE : CarouselVariant.PRODUCT_TILE_NORMAL;
        }
    };
    const carouselConfig = {
        variant: getCarouselVariant(),
        arrowsConfig: {
            color: SliderNavColor.BASE,
            variant: SliderNavVariant.CIRCLE,
        },
        showPagination: false,
        fullWidth: !collapsible && !basketPopup,
        configurationOverrides: {
            preventClicks: true,
            slidesPerView: 'auto',
            spaceBetween: SLIDE_SPACING.MOBILE,
            watchOverflow: true,
            shouldSwiperUpdate: true,
            breakpoints: {
                [MIN_SCREEN_WIDTH_TABLET]: {
                    slidesPerView: ITEMS_AMOUNT_TABLET,
                    spaceBetween: SLIDE_SPACING.TABLET,
                },
                [MIN_SCREEN_WIDTH_DESKTOP]: {
                    slidesPerView: inBasketOverview && basketStatus !== BasketStatus.EMPTY ? 'auto' : ITEMS_AMOUNT_DESKTOP,
                    spaceBetween: inBasketOverview && basketStatus !== BasketStatus.EMPTY ? SLIDE_SPACING.TABLET : SLIDE_SPACING.DESKTOP,
                },
                [MIN_SCREEN_WIDTH_DESKTOP_LARGE]: {
                    slidesPerView: inBasketOverview && basketStatus !== BasketStatus.EMPTY ? 'auto' : ITEMS_AMOUNT_DESKTOP_LARGE,
                    spaceBetween: SLIDE_SPACING.DESKTOP,
                },
            },
        },
    };
    const mainBoxParams = removeOuterSpacing
        ? {}
        : {
            paddingBlockEnd: {
                base: basketPopup ? '8' : '0',
                md: basketPopup ? '8' : '12',
                lg: basketPopup ? '8' : '14',
            },
            paddingBlockStart: {
                base: inBasketOverview ? '3' : collapsible ? '0' : '4',
                md: '0',
            },
        };
    const innerBoxParams = {
        paddingBlockEnd: {
            base: inBasketOverview ? '10' : '6',
            md: '0',
        },
        paddingBlockStart: {
            base: inBasketOverview ? '6' : collapsible ? '0' : '4',
            md: '0',
        },
    };
    const getTitle = () => currentMobileState && !inBasketOverview ? (_jsx(TextAlpha, { strong: true, children: title })) : (_jsx(TextAlpha, { as: "h2", variant: inBasketOverview ? 'subheadingXL' : 'subheadingL', children: title }));
    if (collapsible) {
        return (_jsx(Box, Object.assign({ innerRef: productGroupRef }, mainBoxParams, { "data-analytics-bannerid": analyticsAreaName, "data-monetate-action-id": monetateId, testId: generateTestId({ testId: TEST_ID, suffix: analyticsAreaName }), children: _jsx(Collapse, { id: `${productGroupId}-collapse${title ? `-${title.replace(/ /g, '')}` : ''}`, title: title, open: !collapsed, query: [MediaQuery.MOBILE, MediaQuery.TABLET_SMALL], mobileFullwidth: true, tabletFullwidth: true, desktopFullwidth: true, children: _jsx(Box, { paddingBlockStart: { md: inBasketOverview ? '8' : '6', lg: inBasketOverview ? '10' : '6' }, children: _jsx(Box, Object.assign({}, innerBoxParams, { testId: generateTestId({ testId: TEST_ID, suffix: 'carousel-content' }), children: _jsx(Carousel, Object.assign({}, carouselConfig, { children: slides })) })) }) }) })));
    }
    return (_jsxs(Box, Object.assign({ innerRef: productGroupRef }, mainBoxParams, { "data-analytics-bannerid": analyticsAreaName, "data-monetate-action-id": monetateId, testId: generateTestId({ testId: TEST_ID, suffix: analyticsAreaName }), children: [title && (basketPopup ? _jsx(Box, { paddingInline: { base: '4', md: '8' }, children: getTitle() }) : getTitle()), _jsx(Box, { paddingBlockStart: { md: basketPopup ? '4' : inBasketOverview ? '8' : '6', lg: inBasketOverview ? '10' : '6' }, children: _jsx(Box, Object.assign({}, innerBoxParams, { testId: generateTestId({ testId: TEST_ID, suffix: 'carousel-content' }), children: _jsx(Carousel, Object.assign({}, carouselConfig, { children: slides })) })) })] })));
};
